import { GameDayModel } from '../../../models/gameday-model';
import { AssetsUnderManagement, Account } from '../../models/profile';
import { DonutChartData } from '../donut-chart/donut-chart.component';

export const EMPTY_COLOR = '#9e9e9e';
export const MILLION = 1000000;
export const MUNICIPAL_TYPE = 'Municipal Bonds';
const MUNICIPAL_THICKNESS = 15;

export const PORTFOLIO_OVERVIEW_COLORS: { [key: string]: string } = {
  'Municipal Bonds': '#f78e1e',
  Corporate: '#46d3be',
  Government: '#844fb2',
  'Synd. Loans': '#24779b',
  MBS: '#cad147',
  ABS: '#1fadff',
  Other: '#bc2f80'
};

export const SORTED_SECURITY_TYPES: string[] = [
  MUNICIPAL_TYPE,
  'Corporate',
  'Government',
  'Synd. Loans',
  'MBS',
  'ABS',
  'Other'
];

const PORTFOLIO_CHANGE_INDICATOR_COLORS: { [key: number]: string } = {
  1: 'green',
  0: 'black',
  [-1]: 'red'
};

export const PORTFOLIO_OVERVIEW_EMPTY_SLICE: DonutChartData = {
  value: 0,
  color: EMPTY_COLOR
};

export const PORTFOLIO_OVERVIEW_EMPTY_SLICES: DonutChartData[] = [
  ...SORTED_SECURITY_TYPES.map(type => ({
    value: 0,
    color: getAssetColor(type)
  })),
  {
    value: 1,
    color: EMPTY_COLOR
  }
];

export function getFirmId(
  orders: GameDayModel.Order[],
  account: string
): number | undefined {
  return (
    orders.find(order => order.accountName === account) ||
    ({} as GameDayModel.Order)
  ).firmId;
}

function setDefaultAsset(asset: AssetsUnderManagement): AssetsUnderManagement {
  return {
    ...asset,
    sharePercentageByAssetType: asset.sharePercentageByAssetType || 0,
    reportedAssetsAmount: asset.reportedAssetsAmount || 0,
    securityCount: asset.securityCount || 0,
    aggregateChangePercentage: asset.aggregateChangePercentage || 0,
    parentSecurityType: asset.parentSecurityType || ''
  };
}

export function getEmptyAssets(): AssetsUnderManagement[] {
  return SORTED_SECURITY_TYPES.map(type => ({
    ...new AssetsUnderManagement(),
    parentSecurityType: type
  }));
}

export function getAssetColor(type: string): string {
  return PORTFOLIO_OVERVIEW_COLORS[type];
}

function divOn(input: number | any, on: number): number | any {
  return typeof input === 'number' ? input / on : input;
}

export function assetsStatus([allAssets, municipal, totalAssets]: [
  AssetsUnderManagement[],
  AssetsUnderManagement,
  number
]): { noData: boolean; noAssets: boolean } {
  const allAssetsEmpty = areAllAssetsEmpty(allAssets);
  const municipalEmpty = isAssetEmpty(municipal);
  const totalAssetsEmpty = !totalAssets || totalAssets === 0;
  const noData = allAssetsEmpty && municipalEmpty && totalAssetsEmpty;

  return {
    noData,
    noAssets: !noData && allAssetsEmpty
  };
}

function isAssetEmpty(asset: AssetsUnderManagement) {
  return !asset || !asset.sharePercentageByAssetType;
}

function areAllAssetsEmpty(assets: AssetsUnderManagement[]) {
  return !assets || assets.filter(asset => !isAssetEmpty(asset)).length === 0;
}

// Account mappers
export function accountAssetsMapper(account: Account): AssetsUnderManagement[] {
  if (!account || !account.assetsUnderManagement) {
    return undefined;
  }

  const assets: AssetsUnderManagement[] = [];

  account.assetsUnderManagement.forEach((asset: AssetsUnderManagement) => {
    const index = SORTED_SECURITY_TYPES.indexOf(asset.parentSecurityType);
    assets[index] = asset;
  });

  return assets;
}

export function accountTotalAssetsMapper(account: Account): number | any {
  return divOn(account && account.reportedTotalAssets, MILLION);
}

function getMuniAsset(asset: AssetsUnderManagement): AssetsUnderManagement {
  if (!asset || typeof asset.reportedAssetsAmount !== 'number') {
    return new AssetsUnderManagement();
  }
  const muniAsset: AssetsUnderManagement = setDefaultAsset(asset);
  muniAsset.reportedAssetsAmount = divOn(
    muniAsset.reportedAssetsAmount,
    MILLION
  );

  return muniAsset;
}

export function accountMunicipalAssetsMapper(
  account: Account
): AssetsUnderManagement {
  if (!account || !account.assetsUnderManagement) {
    return getMuniAsset(undefined);
  }

  const municipalAsset: AssetsUnderManagement = account.assetsUnderManagement.find(
    (asset: AssetsUnderManagement): boolean =>
      asset.parentSecurityType === MUNICIPAL_TYPE
  ) as AssetsUnderManagement;

  return getMuniAsset(municipalAsset);
}

// Assets mappers
export function municipalAssetColorMapper(
  asset: AssetsUnderManagement
): string {
  return PORTFOLIO_CHANGE_INDICATOR_COLORS[
    municipalAssetPercentSignMapper(asset)
  ];
}

export function municipalAssetPercentSignMapper(
  asset: AssetsUnderManagement
): number {
  return Math.sign(asset.aggregateChangePercentage);
}

export function assetsToDonutChartDataMapper(
  assets: AssetsUnderManagement[]
): DonutChartData[] {
  if (!assets) {
    return PORTFOLIO_OVERVIEW_EMPTY_SLICES;
  }

  return assets
    .map(
      (asset: AssetsUnderManagement): DonutChartData => ({
        value: asset.reportedAssetsAmount,
        color: getAssetColor(asset.parentSecurityType),
        thickness:
          asset.parentSecurityType === MUNICIPAL_TYPE
            ? MUNICIPAL_THICKNESS
            : undefined
      })
    )
    .concat(PORTFOLIO_OVERVIEW_EMPTY_SLICE);
}

export function assetsMapper(
  assets: AssetsUnderManagement[]
): AssetsUnderManagement[] {
  if (!assets) {
    assets = getEmptyAssets();
  } else {
    assets = assets.map(setDefaultAsset);
  }

  return assets.filter(asset => asset.parentSecurityType !== MUNICIPAL_TYPE);
}
